import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Select, { components } from "react-select";
import Search from "../../components/Header/Search/Search";
import Pagination from "../../components/Pagination/Pagination";
import EmptyState from "../../components/EmptyState";
import userRoleAuth from "../../utils/userRoleAuth/userRoleAuth";
import userRoles from "../../config/userRoles";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  useGetLocationList,
  useGetDepartmentList,
  useGetDesignationList,
} from "../../queries/employeeQueries";
import PageLoader from "../../components/PageLoader";
import EmployeeBankGenerated from "./EmployeeBankGenerated";
import { useGetFetchEmployeeBankPayments } from "../../queries/payrollQuries";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useNavigate } from "react-router-dom";
const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <span className="ico-down text-blue-600"></span>
  </components.DropdownIndicator>
);
// Consolidated report
const ViewEmployeeBankDetails = () => {
  const [searchParams] = useSearchParams();
  const monthParam = searchParams.get("month");
  const yearParam = searchParams.get("year");
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.auth);
  const isHr = userRoleAuth(user, userRoles.HR);
  const axiosPrivate = useAxiosPrivate();
  const searchRef = useRef();
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const selectedMonth = monthParam ? Number(monthParam) : null;
  const selectedYear = yearParam ? Number(yearParam) : null;
  const [searchFilter, setFilter] = useState({
    page_no: 1,
    page_size: 10,
    filter: "",
    department: "",
    designation: "",
    office: "",
  });

  const { data: payrollData, isLoading } = useGetFetchEmployeeBankPayments({
    ...searchFilter,
    month: selectedMonth || "",
    year: selectedYear || "",
  });
  const employeeList = Array.isArray(payrollData?.data) ? payrollData.data : [];
  const totalRecords = Number(payrollData?.totalRecords) || 0;
  const totalPages = Number(payrollData?.totalPages) || 1;

  const [selectedDesignation, setSelectedDesignation] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(null);

  const isFiltered =
    searchFilter.filter ||
    searchFilter.department ||
    searchFilter.designation ||
    searchFilter.office;

  const defaultLocationOption = { location_name: "Office: All", id: null };
  const defaultDesignationOption = {
    designation_title: "Designation: All",
    id: null,
  };
  const defaultDepartmentOption = {
    department_name: "Department: All",
    id: null,
  };

  const { data: getLocationList } = useGetLocationList();
  const { data: getDepartmentList } = useGetDepartmentList();
  const { data: getDesignationList } = useGetDesignationList();

  const departmentList = Array.isArray(getDepartmentList?.result)
    ? [defaultDepartmentOption, ...getDepartmentList.result]
    : [];

  const designationList = Array.isArray(getDesignationList?.result)
    ? [defaultDesignationOption, ...getDesignationList.result]
    : [];

  const locationList = Array.isArray(getLocationList?.result)
    ? [defaultLocationOption, ...getLocationList.result]
    : [];

  const handleSetFilter = (value, source) => {
    setFilter((prev) => ({
      ...prev,
      [source]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilter({
      page_no: 1,
      page_size: 10,
      filter: "",
      department: "",
      designation: "",
      office: "",
    });

    setSelectedDesignation(null);
    setSelectedDepartment(null);
    setSelectedOffice(null);
    if (searchRef.current) searchRef.current.value = "";
  };
  const handleDownloadBankStatement = async () => {
    try {
      const response = await axiosPrivate.get(
        "/payroll/fetchEmployeeBankPayments",
        {
          params: {
            ...searchFilter,
            month: selectedMonth || "",
            year: selectedYear || "",
            download: 1,
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([response?.data], {
        type: "text/csv;charset=utf-8",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `employee_bank_report_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      if (!response?.status) {
        toast.error("No data available to download");
      } else {
        toast.success("Downloaded successfully");
      }
    } catch (error) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="px-10 py-6">
        <div className="flex justify-between items-center pb-4">
          <div>
            <h2>Bank Statement</h2>
            <p className="pb-4">View employee's bank information</p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-3 items-center">
            <Select
              value={selectedDesignation}
              onChange={(selected) => {
                setSelectedDesignation(selected);
                handleSetFilter(selected?.value, "designation");
              }}
              placeholder={"Designation: All"}
              options={designationList.map((i) => ({
                label: i.designation_title,
                value: i.id,
              }))}
              components={{ DropdownIndicator }}
              className="custom-select-container"
            />
            <Select
              value={selectedDepartment}
              onChange={(selected) => {
                setSelectedDepartment(selected);
                handleSetFilter(selected?.value, "department");
              }}
              placeholder={"Department: All"}
              options={departmentList.map((i) => ({
                label: i.department_name,
                value: i.id,
              }))}
              components={{ DropdownIndicator }}
              className="custom-select-container"
            />
            <Select
              value={selectedOffice}
              onChange={(selected) => {
                setSelectedOffice(selected);
                handleSetFilter(selected?.value, "office");
              }}
              placeholder={"Office: All"}
              options={locationList.map((i) => ({
                label: i.location_name,
                value: i.id,
              }))}
              components={{ DropdownIndicator }}
              className="custom-select-container"
            />

            {isFiltered && (
              <span
                className="text-blue-800 font-medium cursor-pointer"
                onClick={handleClearFilters}
              >
                Clear Filters
              </span>
            )}
          </div>

          <Search
            eleRef={searchRef}
            onChange={(e) => handleSetFilter(e, "filter")}
            value={searchFilter.filter}
          />
        </div>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : employeeList.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <EmployeeBankGenerated
            employeeList={employeeList}
            selectedEmployees={selectedEmployees}
            setSelectedEmployees={setSelectedEmployees}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
          />
          <div className="flex justify-end gap-4 mt-6 px-10">
            <button
              className="btn btn--border text-sm font-medium"
              onClick={() => navigate(-1)}
            >
              Back
            </button>

            <button
              onClick={() => handleDownloadBankStatement()}
              className="bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded hover:bg-blue-700"
            >
              Download Bank Statement
            </button>
          </div>

          <div className="mt-[65px]">
            <Pagination
              page={{
                pageNo: searchFilter.page_no,
                setPageNo: (val) => handleSetFilter(val, "page_no"),
                pageSize: searchFilter.page_size,
                setPageSize: (val) => handleSetFilter(val, "page_size"),
                totalRecords: totalRecords,
                totalPages: totalPages,
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ViewEmployeeBankDetails;
